import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { message } from 'antd';

/** Token 在本地存储中的键名 */
export const TOKEN_KEY = 'wukong_admin_token';
/** 管理员信息在本地存储中的键名 */
export const ADMIN_KEY = 'wukong_admin_info';

// 后端API地址（部署到Cloudflare Workers）
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://lvdou-api.pages.dev/api';

// 创建 axios 实例
const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器：自动附加 token
client.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// 响应拦截器：统一处理错误
client.interceptors.response.use(
  (response) => {
    const res = response.data;
    // 检查是否为静默模式（采集接口用，错误不弹 toast，由进度面板统一展示）
    const silent = (response.config as any)?._silent === true;
    // 约定 code===1 为成功，其余为业务错误
    if (res && typeof res === 'object' && 'code' in res) {
      if (res.code === 1) {
        return res;
      }
      // 未登录 / token 失效
      if (res.code === 1001 || res.code === 401) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(ADMIN_KEY);
        // 避免在登录页重复跳转
        if (!window.location.pathname.includes('/login')) {
          message.error('登录已失效，请重新登录');
          window.location.href = '/login';
        }
        return Promise.reject(new Error(res.msg || '未登录'));
      }
      if (!silent) message.error(res.msg || '请求失败');
      return Promise.reject(new Error(res.msg || '请求失败'));
    }
    return res;
  },
  (error: AxiosError) => {
    // 检查是否为静默模式
    const silent = (error.config as any)?._silent === true;
    if (error.response) {
      const status = error.response.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(ADMIN_KEY);
        if (!window.location.pathname.includes('/login')) {
          message.error('登录已失效，请重新登录');
          window.location.href = '/login';
        }
      } else if (!silent) {
        if (status >= 500) {
          message.error('服务器异常，请稍后重试');
        } else {
          message.error(`请求错误：${status}`);
        }
      }
    } else if (error.request) {
      if (!silent) message.error('网络异常，请检查网络连接');
    } else {
      if (!silent) message.error(error.message || '请求失败');
    }
    return Promise.reject(error);
  },
);

/** 通用 GET 请求 */
export function get<T = unknown>(url: string, params?: Record<string, unknown>, config?: AxiosRequestConfig) {
  return client.get<unknown, { code: number; msg: string; data: T }>(url, { params, ...config });
}

/** 通用 POST 请求 */
export function post<T = unknown>(url: string, data?: Record<string, unknown>, config?: AxiosRequestConfig) {
  return client.post<unknown, { code: number; msg: string; data: T }>(url, data, config);
}

/** 通用 DELETE 请求 */
export function del<T = unknown>(url: string, params?: Record<string, unknown>, config?: AxiosRequestConfig) {
  return client.delete<unknown, { code: number; msg: string; data: T }>(url, { params, ...config });
}

export default client;
